// hooks/use-keyboard-navigation.ts
import { useEffect } from 'react'

import { useNavigate } from 'react-router-dom'

/**
 * Represents a keyboard shortcut for navigation.
 *
 * @typedef {Object} KeyboardShortcut
 * @property {string} key - The key that triggers the shortcut.
 * @property {boolean} [metaKey] - Indicates if the meta key (e.g., Command key on Mac) must be held.
 * @property {boolean} [ctrlKey] - Indicates if the control key must be held.
 * @property {boolean} [shiftKey] - Indicates if the shift key must be held.
 * @property {boolean} [altKey] - Indicates if the alt key must be held.
 * @property {string} path - The path to navigate to when the shortcut is activated.
 */

/**
 * Hook to enable keyboard navigation shortcuts.
 *
 * @param {KeyboardShortcut[]} shortcuts - An array of keyboard shortcuts.
 */
export function useKeyboardNavigation(shortcuts) {
  const navigate = useNavigate()

  useEffect(() => {
    const handleKeyDown = event => {
      shortcuts.forEach(({ key, metaKey, ctrlKey, shiftKey, altKey, path }) => {
        const metaMatch = metaKey === undefined || event.metaKey === metaKey
        const ctrlMatch = ctrlKey === undefined || event.ctrlKey === ctrlKey
        const shiftMatch = shiftKey === undefined || event.shiftKey === shiftKey
        const altMatch = altKey === undefined || event.altKey === altKey
        const keyMatch = event.key.toUpperCase() === key.toUpperCase()

        if (metaMatch && ctrlMatch && shiftMatch && altMatch && keyMatch) {
          event.preventDefault()
          navigate(path)
        }
      })
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [navigate, shortcuts])
}
export function useKeyboardShortcut(shortcuts) {
  useEffect(() => {
    const handleKeyDown = event => {
      shortcuts.forEach(
        ({ key, metaKey, ctrlKey, shiftKey, altKey, callback }) => {
          const metaMatch = metaKey === undefined || event.metaKey === metaKey
          const ctrlMatch = ctrlKey === undefined || event.ctrlKey === ctrlKey
          const shiftMatch =
            shiftKey === undefined || event.shiftKey === shiftKey
          const altMatch = altKey === undefined || event.altKey === altKey
          const keyMatch = event.key.toUpperCase() === key.toUpperCase()

          if (metaMatch && ctrlMatch && shiftMatch && altMatch && keyMatch) {
            event.preventDefault()
            callback(event)
          }
        },
      )
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [shortcuts])
}

export const getModifierKey = os => {
  const keys = {
    mac: { meta: '⌘', alt: '⌥', shift: '⇧', ctrl: '⌃' },
    windows: { meta: 'Ctrl', alt: 'Alt', shift: 'Shift', ctrl: 'Ctrl' },
    linux: { meta: 'Ctrl', alt: 'Alt', shift: 'Shift', ctrl: 'Ctrl' },
  }

  return keys[os] || keys.windows
}
